import { useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

const EDGE_THRESHOLD = 30; // px from left edge to initiate
const MIN_SWIPE_DISTANCE = 100; // px to trigger navigation
const VELOCITY_THRESHOLD = 0.4; // px/ms for fast swipes

export const useSwipeBack = (backPath: string) => {
  const history = useHistory();
  const containerRef = useRef<HTMLDivElement>(null);
  const touchRef = useRef<{
    startX: number;
    startY: number;
    startTime: number;
    scrollY: number;
    swiping: boolean;
  } | null>(null);

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch.clientX <= EDGE_THRESHOLD) {
        touchRef.current = {
          startX: touch.clientX,
          startY: touch.clientY,
          startTime: Date.now(),
          scrollY: window.scrollY,
          swiping: false,
        };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const state = touchRef.current;
      const container = containerRef.current;
      if (!state || !container) return;

      const touch = e.touches[0];
      const dx = touch.clientX - state.startX;
      const dy = Math.abs(touch.clientY - state.startY);

      // If vertical movement dominates, cancel the swipe
      if (!state.swiping && dy > Math.abs(dx)) {
        touchRef.current = null;
        return;
      }

      if (dx > 10) {
        state.swiping = true;

        // Lock vertical scroll position
        e.preventDefault();
        window.scrollTo(0, state.scrollY);

        const progress = Math.min(dx / window.innerWidth, 1);
        container.style.transition = 'none';
        container.style.transform = `translateX(${dx}px)`;
        container.style.opacity = `${1 - progress * 0.5}`;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      const state = touchRef.current;
      const container = containerRef.current;
      if (!state || !container) {
        touchRef.current = null;
        return;
      }

      if (!state.swiping) {
        touchRef.current = null;
        return;
      }

      const touch = e.changedTouches[0];
      const dx = touch.clientX - state.startX;
      const elapsed = Date.now() - state.startTime;
      const velocity = dx / elapsed;

      if (dx > MIN_SWIPE_DISTANCE || velocity > VELOCITY_THRESHOLD) {
        // Swipe succeeded — animate off screen then navigate
        container.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out';
        container.style.transform = `translateX(${window.innerWidth}px)`;
        container.style.opacity = '0';
        setTimeout(() => {
          history.push(backPath);
        }, 180);
      } else {
        // Snap back
        container.style.transition = 'transform 0.25s ease-out, opacity 0.25s ease-out';
        container.style.transform = '';
        container.style.opacity = '';
      }

      touchRef.current = null;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    // passive: false needed to call preventDefault() for scroll lock
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [backPath, history]);

  return containerRef;
};
